import {useCallback} from "react";
import {toast, UpdateOptions} from "react-toastify";
import {useTranslation} from "react-i18next";
import {toastSetting} from "../toastSettingObject";
import {BACKEND} from "../settings";
import {useDownload} from "./useDownload";
import {useTaskPolling, TaskStatus, TaskPollingError} from "./useTaskPolling";

const POLL_INTERVAL = 2000;
const MAX_POLL_ATTEMPTS = 150;

export const useAsyncDownloadWithToast = () => {
  const {t} = useTranslation();
  const {download} = useDownload();
  const {startPolling, stopPolling} = useTaskPolling();

  const startExport = useCallback(
    async (url: string, fileName: string) => {
      const toastId = toast.loading(t("export.starting"), {
        position: "bottom-right",
      });

      let taskId: string | undefined;

      try {
        const response = await fetch(url);

        if (!response.ok) {
          if (response.status === 429) {
            throw new Error(t("export.rateLimited"));
          }
          throw new Error(t("export.startFailed"));
        }

        const data: TaskStatus = await response.json();

        if (data.status !== "processing" || !data.task_id) {
          throw new Error(t("export.invalidResponse"));
        }

        taskId = data.task_id;

        toast.update(toastId, {
          render: t("export.initiated"),
          type: "info",
          isLoading: true,
        });

        const statusUrl = `${BACKEND}/_/async/export/task/${taskId}/status/`;

        startPolling(statusUrl, taskId, {
          pollInterval: POLL_INTERVAL,
          maxAttempts: MAX_POLL_ATTEMPTS,
          onProgress: (progress) => {
            toast.update(toastId, {
              render: t("export.processing"),
              type: "info",
              isLoading: true,
            });
          },
          onCompleted: async (completedTaskId) => {
            toast.update(toastId, {
              render: t("export.downloading"),
              type: "info",
              isLoading: true,
            });

            const downloadUrl = `${BACKEND}/_/async/export/task/${completedTaskId}/download/`;
            await download(downloadUrl, fileName);

            toast.update(toastId, {
              render: t("download.success"),
              ...(toastSetting.success as UpdateOptions),
            });
          },
          onError: (error: TaskPollingError) => {
            let errorMessage: string;

            switch (error.code) {
              case "TIMEOUT":
                errorMessage = t("export.timeout");
                break;
              case "CHECK_FAILED":
              case "PROCESSING_FAILED":
              default:
                errorMessage = t("export.failed");
                break;
            }

            toast.update(toastId, {
              render: errorMessage,
              ...(toastSetting.error as UpdateOptions),
            });
          },
        });
      } catch (error) {
        if (taskId) {
          stopPolling(taskId);
        }
        toast.update(toastId, {
          render: error instanceof Error ? error.message : t("export.failed"),
          ...(toastSetting.error as UpdateOptions),
        });
      }
    },
    [t, download, startPolling, stopPolling],
  );

  return {handleAsyncExport: startExport};
};
